import { api, fmtDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Paperclip } from "lucide-react";

export const TYPE_LABEL = { CL: "Casual Leave", SL: "Sick Leave", EL: "Earned Leave" };
export const TYPES = ["CL", "SL", "EL"];

const STATUS_STYLE = {
  Pending: "bg-[#FEF3C7] text-[#92400E]",
  Approved: "bg-[#D1FAE5] text-[#065F46]",
  Rejected: "bg-[#FEE2E2] text-[#991B1B]",
  Cancelled: "bg-[#F3F4F6] text-[#4B5563]",
};

export const fmtDays = (n) => (n == null ? "—" : Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(1));

export const StatusBadge = ({ app }) => (
  <div className="flex flex-col gap-1 items-start">
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${STATUS_STYLE[app.status] || ""}`} data-testid={`leave-status-${app.id}`}>
      {app.status}{app.status === "Pending" && app.stage ? ` · ${app.stage}` : ""}
    </span>
    {app.clarification_requested && <span className="text-[9px] uppercase tracking-widest font-bold text-[#B45309]">Clarification needed</span>}
    {app.cancellation_requested && <span className="text-[9px] uppercase tracking-widest font-bold text-[#B91C1C]">Cancel requested</span>}
  </div>
);

export async function downloadBlob(url, filename) {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

export async function openAttachment(id) {
  const r = await api.get(`/leave/applications/${id}/attachment`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
}

const Row = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-[#F3F4F6] text-sm">
    <div className="label-eyebrow pt-0.5">{label}</div>
    <div className="col-span-2 break-words">{children || "—"}</div>
  </div>
);

export const LeaveDetailsDialog = ({ app, onClose, children }) => (
  <Dialog open={!!app} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="leave-details-dialog">
      {app && (
        <>
          <DialogHeader><DialogTitle className="font-black tracking-tight">{TYPE_LABEL[app.leave_type]} · {app.employee_name}</DialogTitle></DialogHeader>
          <div>
            <Row label="Status"><StatusBadge app={app} /></Row>
            <Row label="Employee">{app.employee_name} {app.employee_code && `(${app.employee_code})`} · {app.department || "—"} · {app.designation || "—"}</Row>
            <Row label="Dates">{fmtDate(app.from_date)} → {fmtDate(app.to_date)} · {app.duration === "half" ? "Half day" : "Full day"}</Row>
            <Row label="Days">{fmtDays(app.days)}</Row>
            <Row label="Reason">{app.reason}</Row>
            <Row label="Contact">{app.contact_number}</Row>
            <Row label="Attachment">
              {app.attachment_path ? (
                <button className="text-[#002FA7] font-bold inline-flex items-center gap-1 hover:underline" onClick={() => openAttachment(app.id)} data-testid="leave-attachment-open">
                  <Paperclip className="w-3.5 h-3.5" /> {app.attachment_name || "View"}
                </button>
              ) : null}
            </Row>
            <Row label="Decided by">{app.decided_by ? `${app.decided_by} · ${fmtDate(app.decided_at)}` : null}</Row>
            <Row label="Remarks">{app.hr_remarks}</Row>
            {app.clarification_note && <Row label="Clarification asked">{app.clarification_note}</Row>}
            {app.clarification_response && <Row label="Employee response">{app.clarification_response}</Row>}
            {app.cancellation_reason && <Row label="Cancel reason">{app.cancellation_reason}</Row>}
          </div>
          <div className="mt-3">
            <div className="label-eyebrow mb-2">History</div>
            <div className="space-y-1">
              {(app.history || []).map((h, i) => (
                <div key={i} className="text-xs text-[#4B5563]">
                  <span className="font-mono text-[#9CA3AF]">{new Date(h.at).toLocaleString()}</span> · <b>{h.event}</b> · {h.by}{h.remarks ? ` — ${h.remarks}` : ""}
                </div>
              ))}
            </div>
          </div>
          {children}
        </>
      )}
    </DialogContent>
  </Dialog>
);
